import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { API_URL } from "../constants"

// Get user from localStorage
const userInfo = localStorage.getItem("userInfo") ? JSON.parse(localStorage.getItem("userInfo")) : null

const initialState = {
  userInfo,
  loading: false,
  error: null,
  success: false,
}

// Register user
export const register = createAsyncThunk("auth/register", async (userData, { rejectWithValue }) => {
  try {
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    }

    const { data } = await axios.post(`${API_URL}/api/auth/register`, userData, config)

    return data
  } catch (error) {
    return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message)
  }
})

// Login user
export const login = createAsyncThunk("auth/login", async (userData, { rejectWithValue }) => {
  try {
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    }

    const { data } = await axios.post(`${API_URL}/api/auth/login`, userData, config)

    // Save user to localStorage
    localStorage.setItem("userInfo", JSON.stringify(data))

    return data
  } catch (error) {
    return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message)
  }
})

// Logout user
export const logout = createAsyncThunk("auth/logout", async () => {
  localStorage.removeItem("userInfo")
  return null
})

// Update user profile
export const updateProfile = createAsyncThunk("auth/updateProfile", async (userData, { getState, rejectWithValue }) => {
  try {
    const {
      auth: { userInfo },
    } = getState()

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const { data } = await axios.put(`${API_URL}/api/users/profile`, userData, config)

    // Update localStorage
    const updatedUserInfo = {
      ...userInfo,
      user: {
        ...userInfo.user,
        ...data,
      },
    }
    localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo))

    return data
  } catch (error) {
    console.error("Profile update error:", error.response || error)
    return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message)
  }
})

// New: Fetch logged-in user's status and log out if suspended
export const fetchLoggedInUserStatus = createAsyncThunk(
  "auth/fetchLoggedInUserStatus",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      if (!userInfo || !userInfo.token) {
        // No user info or token, nothing to check, just return
        return
      }

      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.get(`${API_URL}/api/users/profile`, config)

      if (data.status === "suspended" || data.status === "deleted") {
        dispatch(logout()) // Dispatch logout if suspended or deleted
        return rejectWithValue("Your account has been suspended or deleted. You have been logged out.")
      }

      // IMPORTANT: If the user is active, DO NOT update userInfo in Redux state here.
      // This prevents an infinite loop where App.js's useEffect re-dispatches this thunk
      // because userInfo object reference changes.
      // The userInfo in Redux should primarily be updated by login and updateProfile thunks.
      // This thunk's purpose is only to check for suspension.
      return data // Still return data, but it won't trigger state update in extraReducers for fulfilled case
    } catch (error) {
      console.error("Error fetching user status:", error.response || error)
      // If there's an error (e.g., token expired, unauthorized), log out
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        dispatch(logout())
        return rejectWithValue("Session expired or unauthorized. Please log in again.")
      }
      return rejectWithValue(
        error.response && error.response.data.message ? error.response.data.message : error.message,
      )
    }
  },
)

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetAuthState: (state) => {
      state.loading = false
      state.error = null
      state.success = false
    },
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false
        state.success = true
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false
        state.userInfo = action.payload
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.userInfo = null
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false
        if (state.userInfo && state.userInfo.user) {
          state.userInfo.user = {
            ...state.userInfo.user,
            ...action.payload,
          }
        }
        state.success = true
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Fetch Logged In User Status
      .addCase(fetchLoggedInUserStatus.pending, (state) => {
        // No loading indicator for this background check
        state.error = null
      })
      .addCase(fetchLoggedInUserStatus.fulfilled, (state, action) => {
        // IMPORTANT: Do NOT update state.userInfo here if the user is active.
        // This prevents the infinite loop. The userInfo in the state should
        // only be updated by login.fulfilled and updateProfile.fulfilled.
        // This thunk's primary role is to trigger logout if suspended.
        // If action.payload is undefined, it means the user was not logged in
        // or was suspended and logout was already dispatched within the thunk.
      })
      .addCase(fetchLoggedInUserStatus.rejected, (state, action) => {
        // Error handled by logout dispatch within the thunk
        // Optionally, you can set a specific error message here if needed for UI feedback
        // state.error = action.payload;
      })
  },
})

export const { resetAuthState } = authSlice.actions

export default authSlice.reducer
